import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';
import { CardAPI, FetchAPI, LayoutAPI, Link, ModalService, PanelAPI, TextAPI, ToastService, Tooltip } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { LanguageService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation/lib/context';

import { ReactComponent as ScheduleSVG } from '../../../resources/assets/commons/calendar.svg';
import { ReactComponent as CriticalSVG } from '../../../resources/assets/commons/information-circle.svg';
import { DashboardAliases, FragmentNames } from '../../codegen';
import { GeneratedOsRoutesApplication, KpiOpenedRoutesApplication, MessageRoutesApplication, ResolutionTimeRoutesApplication, UserRoutesApplication } from '../../constants';
import { Maintenance } from '../../interfaces';
import { TermService, TermType, UserService } from '../../services';
import { TempCredentialService } from '../../services/temp-credentials';
import BaseController from '../BaseController';
import { SYNC_TERMS_DIALOG_ID } from '../term-use-controller';
import './dashboard-controller.css';

@Controller({ fragment: FragmentNames.DASHBOARD })
export default class DashboardController extends BaseController {

  @Inject(DashboardAliases.FETCH_MESSAGES)
  fetchMessages!: FetchAPI;

  @Inject(DashboardAliases.PANEL_TOP)
  panelTop!: PanelAPI;

  @Inject(DashboardAliases.FETCH_0)
  fetch1 !: FetchAPI;

  @Inject(DashboardAliases.FETCH_0)
  fetch2 !: FetchAPI;
  
  @Inject(DashboardAliases.FETCH_0)
  fetch3 !: FetchAPI;

  @Inject(DashboardAliases.MAIN_CARD_CONTAINER)
  card1 !: CardAPI;

  @Inject(DashboardAliases.MAIN_CARD_CONTAINER_1_DECK_01)
  card2 !: CardAPI;

  @Inject(DashboardAliases.MAIN_CARD_CONTAINER_1_DECK_02)
  card3 !: CardAPI;
  
  vFatch1: any | undefined;
  vFatch2: any | undefined;
  vFatch3: any | undefined;

  hasPermission1: boolean;
  hasPermission2: boolean;
  hasPermission3: boolean;

  @OnMount({ target: DashboardAliases.LAYOUT_DASHBOARD })
  onMountLayoutDashboard(): void {

    const id = this.getLoggedUser().id;
    void TempCredentialService.isTempCredentials(id).then(response =>{
      if(response === true){
        applicationContext.redirect(UserRoutesApplication.USER_ITEM+'?tempPass=true', { id });
      }
    });

    const dialogTerms = !!localStorage.getItem('dialog_terms');
    if (!dialogTerms) {
      localStorage.setItem('dialog_terms', 'true');
      
      const event = new CustomEvent('updateModulesRoute', { detail: { moduleId: 'dashboard' } });
      dispatchEvent(event);
      void TermService.isRead(TermType.TERM_OF_USE).then(response => {
        if (!response) {
          ModalService.openDialog({
            id: SYNC_TERMS_DIALOG_ID,
            fragment: 'term-use',
            contextData: {
              type: TermType.TERM_OF_USE,
            },
            onClose: () => {
              localStorage.removeItem('dialog_terms');
              this.openPrivacyNotice();
            },
          });
        } else {
          localStorage.removeItem('dialog_terms');
          this.openPrivacyNotice();
        }
      });
      return;
    }
  }

  @OnMount(DashboardAliases.TEXT_CURRENT_MONTH)
  onMountCurrentMonth(event: MountEvent): void {
    const text = (event.target as TextAPI);
    const month = Intl.DateTimeFormat(LanguageService.getCurrentLanguage(), {
      month: 'long',
    }).format(new Date());
    const expression = MessageService.translate('title.current-month');
    text.setValue(expression.replace('#@MONTH#@', month));
  }

  @OnMount({ target: [DashboardAliases.CARD_MESSAGES, DashboardAliases.CARD_MESSAGES_MOBILE] })
  onMountCardMessage(event: MountEvent): void {
    const card = (event.target as CardAPI);
    if (!this.hasPermission('message')) {
      card.setClassName('hidden-card');
    }
  }

  @OnMount({ target: DashboardAliases.LAYOUT_CENTER })
  onMountLayoutDesktop(event: MountEvent): void {
    const layout = (event.target as LayoutAPI);
    if (!this.hasPermission('message')) {
      layout.setTemplateAreas('"left"');
      layout.setColumns('auto');
    }
  }

  @OnMount({ target: DashboardAliases.LAYOUT_CENTER_MOBILE })
  onMountLayoutMobile(event: MountEvent): void {
    const layout = (event.target as LayoutAPI);
    if (!this.hasPermission('message')) {
      layout.setTemplateAreas('"top"');
      layout.setRows('auto');
    }
  }

  /* --------------- Card-container ------------------*/
  @OnAfterFetch(DashboardAliases.FETCH_0)
  onFetch1(event: FetchEvent): void {
    const data = event.data as Array<any>;
    this.vFatch1 = data[0];
  }

  @OnAfterFetch(DashboardAliases.FETCH_1)
  onFetch2(event: FetchEvent): void {
    const data = event.data as Array<any>;
    this.vFatch2 = data[0];
  }

  @OnAfterFetch(DashboardAliases.FETCH_2)
  onFetch3(event: FetchEvent): void {
    const data = event.data as Array<any>;
    this.vFatch3 = data[0];
  }

  @OnMount(DashboardAliases.MAIN_CARD_CONTAINER)
  onMounCardContainer1(): void {
    this.hasPermission1 = this.vFatch1.hashPermission;
    if (this.vFatch1.hashPermission === false) {
      this.card1.setClassName('hidden-card');
    }
  }

  @OnMount(DashboardAliases.MAIN_CARD_CONTAINER_1_DECK_01)
  onMounCardContainer2(): void {
    this.hasPermission2 = this.vFatch2.hashPermission;
    if (this.vFatch2.hashPermission === false) {
      this.card2.setClassName('hidden-card');
    }
  }

  @OnMount(DashboardAliases.MAIN_CARD_CONTAINER_1_DECK_02)
  onMounCardContainer3(): void {
    this.hasPermission3 = this.vFatch3.hashPermission;
    if (this.vFatch3.hashPermission === false) {
      this.card3.setClassName('hidden-card');
    }
    if( this.hasPermission1 === false 
      && this.hasPermission2 === false 
      && this.hasPermission3 === false) {
      this.panelTop.setVisible(false);
    }
  }

  @OnClick(DashboardAliases.LINK_DECK_0)
  onClickLink0(): void {
    applicationContext.redirect(GeneratedOsRoutesApplication.GENERATED_OS);
  }
  @OnClick(DashboardAliases.LINK_DECK_1)
  onClickLink1(): void {
    applicationContext.redirect(KpiOpenedRoutesApplication.OS_OPENED_GRAPHICS);
  }
  @OnClick(DashboardAliases.LINK_DECK_2)
  onClickLink2(): void {
    applicationContext.redirect(ResolutionTimeRoutesApplication.RESOLUTION_TIME);
  }

  /* --------------- Card-container-end ------------------*/

  openPrivacyNotice(): void {
    const dialogTerms = !!localStorage.getItem('dialog_terms');
    if (!dialogTerms) {
      localStorage.setItem('dialog_terms', 'true');
      void TermService.isRead(TermType.PRIVACY_NOTICE).then(response => {
        if (!response) {
          ModalService.openDialog({
            id: SYNC_TERMS_DIALOG_ID,
            fragment: 'term-use',
            contextData: {
              type: TermType.PRIVACY_NOTICE,
            },
            onClose: () => {
              localStorage.removeItem('dialog_terms');
              this.openSelectCustomer();
            },
          });
        } else {
          localStorage.removeItem('dialog_terms');
          this.openSelectCustomer();
        }
      });
    }
  }

  openSelectCustomer(): void {
    void UserService.hasDefaultCustomer().then(response => {
      if (!response.default) {
        const isHD = window.screen.availWidth > 768;
        const userId = this.getLoggedUserId();
        const message = MessageService.translate('customer.default-selection');
        ToastService.closeAll();
        ToastService.open({
          action: (<Link
            value="text.yes"
            route={`/application/user/${userId}`}
          />),
          dismissExpression: 'text.no',
          dismissible: true,
          highlight: true,
          id: 'dashboard-controller.customer-selection',
          level: ToastLevel.WARNING,
          message: `${response.customerName} - ${message}`,
          offset: 0,
          placement: Placement.BOTTOM,
          showIcon: true,
          type: isHD ? ToastType.BANNER : ToastType.BUBBLE,
        });
      }
    });
  }

  @OnDynamicContentRender({ target: DashboardAliases.TABLE_MAINTENANCE })
  onRenderColumns(event: RenderEvent): void {
    switch (event.entry) {
      case 'critical':
        event.render((data: Maintenance): ReactElement => {
          const actionStyle = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          };
          return (<div>
            <Tooltip value={data.critical ? 'maintenance.critical' : 'maintenance.scheduled'}>
              <div style={actionStyle}>
                {this.renderSVG(data.critical)}
              </div>
            </Tooltip>
          </div>);
        });
        break;
    }
  }

  renderSVG(isCritical: boolean): ReactElement {
    if (isCritical) {
      return (<CriticalSVG fill="#E20101FF"/>);
    }
    return (<ScheduleSVG fill="#1477a9"/>);
  }

  @OnClick(DashboardAliases.MESSAGE_CARD)
  onClickCardMessage(event: ClickEvent): void {
    if (event?.value != null) {
      const { id } = event.value;
      applicationContext.redirect(MessageRoutesApplication.MESSAGE_ITEM, { id });
    }
  }

  @OnClick(DashboardAliases.MESSAGE_CARD_TMP)
  onClickCardMessageTmp(event: ClickEvent): void {
    if (event?.value != null) {
      const { id } = event.value;
      applicationContext.redirect(MessageRoutesApplication.MESSAGE_ITEM, { id });
    }
  }

}
